import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Column, useDevice } from 'seagull';
import { EditableTableList } from 'seagull/components/EditableTableList/EditableTableList';
import { getTestIds } from 'seagull/utils/TestIds';
import { BillingType } from '~/components/BillModal/BillClientModal.types';
import { useBillItems } from '~/components/BillModal/hooks/useBillItems';
import { useLocaleKeys } from '~/i18n';
import { serviceCostModel } from '~/models';
import { costDisplayTextsPath } from '~/models/service-cost/selectors';
import { utils } from '~/utils';
import { billingStagesTextsPath } from '../../utils/lineItem.utils';
import { billClientModalTextsPath } from '../BillServiceTableItem/BillServiceTableItem.utils';
import { ChargeEditSheet } from './ChargeEditSheet';
import { BuildLineColumns } from './columns';
import { itemSelectName } from './columns.utils';
import { RowCallouts } from './RowCallouts';
export const GetBillItemsTableTID = (parentTestId)=>getTestIds(parentTestId);
const EMPTY_DISABLED_ITEMS = [];
export function BillItemsTable(_0) {
    let { includePreapproved = true, relationshipEffectiveDate = null, manualServicesBilling, invoiceDateInfo, hideChargeApprovalCallout = false, itemSelect, disabledItems = EMPTY_DISABLED_ITEMS, disabledRowTooltip, testId } = _0, biProps = _object_without_properties(_0, [
        "includePreapproved",
        "relationshipEffectiveDate",
        "manualServicesBilling",
        "invoiceDateInfo",
        "hideChargeApprovalCallout",
        "itemSelect",
        "disabledItems",
        "disabledRowTooltip",
        "testId"
    ]);
    var _useWatch;
    const { t: billTexts } = useLocaleKeys(billClientModalTextsPath);
    const { t: stageTexts } = useLocaleKeys(billingStagesTextsPath);
    const { t: costDisplayTexts } = useLocaleKeys(costDisplayTextsPath);
    const { BillItemsTableTestIds } = useMemo(()=>GetBillItemsTableTID(testId), [
        testId
    ]);
    const { control, trigger } = useFormContext();
    const { fields, removeItem, moveItem, replaceItem } = useBillItems();
    // WHY: mobile cards are read-only — a newly added charge has no inline inputs, so on mobile we
    // auto-open its edit bottom-sheet; desktop keeps inline editing. User-approved feature branch.
    // eslint-disable-next-line no-restricted-syntax
    const { isMobileDevice } = useDevice();
    const previousFieldCount = useRef(fields.length);
    const tableRef = useRef(null);
    useEffect(()=>{
        var _tableRef_current;
        const grew = fields.length > previousFieldCount.current;
        previousFieldCount.current = fields.length;
        if (isMobileDevice && grew) (_tableRef_current = tableRef.current) === null || _tableRef_current === void 0 ? void 0 : _tableRef_current.openEditRow(fields[fields.length - 1].itemId);
    }, [
        fields,
        isMobileDevice
    ]);
    const watchedItems = (_useWatch = useWatch({
        control,
        name: 'items'
    })) !== null && _useWatch !== void 0 ? _useWatch : [];
    const issueDate = useWatch({
        control,
        name: 'issueDate'
    });
    const mode = useWatch({
        control,
        name: 'mode'
    });
    const formRows = fields.map((field, index)=>{
        var _watchedItems_index;
        return (_watchedItems_index = watchedItems[index]) !== null && _watchedItems_index !== void 0 ? _watchedItems_index : field;
    });
    const rows = [
        ...formRows,
        ...disabledItems
    ];
    const isRowStatic = (_row, rowIndex)=>rowIndex >= formRows.length;
    const columnParams = useMemo(()=>({
            texts: {
                billTexts,
                tableHeaders: billTexts.tableHeaders,
                stageTexts,
                costDisplayTexts
            },
            testIds: BillItemsTableTestIds,
            includePreapproved,
            control,
            trigger,
            manualServicesBilling,
            relationshipEffectiveDate,
            itemSelect,
            disabledRowTooltip
        }), [
        billTexts,
        stageTexts,
        costDisplayTexts,
        BillItemsTableTestIds,
        includePreapproved,
        control,
        trigger,
        manualServicesBilling,
        relationshipEffectiveDate,
        itemSelect,
        disabledRowTooltip
    ]);
    const columns = useMemo(()=>BuildLineColumns(columnParams), [
        columnParams
    ]);
    const editSheetHeader = (item)=>{
        var _ref;
        return item.type === BillingType.Service ? {
            title: item.fullService.name,
            subtitle: utils.formatDollarAmount(serviceCostModel.price(item.fullService.cost))
        } : {
            title: itemSelectName(item, (_ref = itemSelect === null || itemSelect === void 0 ? void 0 : itemSelect.outOfScopeTemplates) !== null && _ref !== void 0 ? _ref : []) || billTexts.addCharge.chooseItemPlaceholder()
        };
    };
    const renderRowFooter = (item, rowIndex)=>item.disabled ? null : /*#__PURE__*/ _jsx(Box, {
            testId: BillItemsTableTestIds.RowFooter(rowIndex),
            children: /*#__PURE__*/ _jsx(RowCallouts, {
                item: item,
                rowIndex: rowIndex,
                mode: mode,
                issueDate: issueDate,
                relationshipEffectiveDate: relationshipEffectiveDate,
                invoiceDateInfo: invoiceDateInfo,
                hideChargeApprovalCallout: hideChargeApprovalCallout,
                manualServiceBilling: item.type === BillingType.Service ? manualServicesBilling === null || manualServicesBilling === void 0 ? void 0 : manualServicesBilling[item.service.id] : undefined,
                testIds: BillItemsTableTestIds
            })
        });
    return /*#__PURE__*/ _jsx(Column, {
        gap: 8,
        children: /*#__PURE__*/ _jsx(EditableTableList, _object_spread({
            ref: tableRef,
            testId: BillItemsTableTestIds.Root(),
            rows: rows,
            columns: columns,
            getRowId: (item)=>item.itemId,
            isRowStatic: isRowStatic,
            disabledRowTooltip: disabledRowTooltip,
            onReorder: moveItem,
            onRemoveRow: removeItem,
            renderRowFooter: renderRowFooter,
            renderEditSheet: (rowItem, rowIndex, param)=>{
                let { close, isAutoOpen } = param;
                return /*#__PURE__*/ _jsx(ChargeEditSheet, {
                    item: rowItem,
                    columnParams: columnParams,
                    mode: mode,
                    issueDate: issueDate,
                    header: editSheetHeader(rowItem),
                    submitLabel: isAutoOpen ? billTexts.addCharge.triggerLabel() : billTexts.ctaLabels.updateLineItem(),
                    submitTestId: BillItemsTableTestIds.EditSheetSubmit(),
                    totalLabel: billTexts.tableHeaders.total(),
                    totalTestId: BillItemsTableTestIds.EditSheetTotal(),
                    onApply: (updated)=>replaceItem(rowIndex, _object_spread_props(_object_spread({}, updated), {
                            itemId: rowItem.itemId
                        })),
                    onClose: close
                });
            }
        }, biProps))
    });
}
